import type {
  BlueprintConnection,
  BlueprintDocument,
  DeploymentResource,
  Diagnostic,
  ResourceType,
  Run,
} from '../api/types';
import { isTerminalRunStatus } from '../api/types';
import { effectiveAlias, wiringRule } from './blueprint';
import { connectionKey } from './diagnostics';
import { parsePointer } from './jsonPointer';

/** Warning a connector reports when a trigger needs the function's code first (spec 13, 5.5). */
export const TRIGGER_WAITING_FOR_CODE = 'TRIGGER_WAITING_FOR_CODE';

/** Incoming `triggers` connections of a resource (a function). */
export function incomingTriggers(doc: BlueprintDocument, resourceId: string): BlueprintConnection[] {
  return (doc.connections ?? []).filter((c) => c.type === 'triggers' && c.targetId === resourceId);
}

/**
 * The most recent finished full apply (runs are listed newest first). Its diagnostics say which triggers
 * still wait for the code. Vault-only applies don't count.
 */
export function latestApply(runs: readonly Run[] | undefined): Run | undefined {
  return (runs ?? []).find(
    (run) => run.kind === 'apply' && (run.scope ?? 'full') === 'full' && isTerminalRunStatus(run.status),
  );
}

/** The alias as a whole word: `Orders` doesn't match inside `Orders_Archive` (`_` is a word character). */
function mentionsWord(message: string, word: string | undefined): boolean {
  if (!word) return false;
  return new RegExp(`(^|[^A-Za-z0-9_])${word}($|[^A-Za-z0-9_])`).test(message);
}

/**
 * Maps `TRIGGER_WAITING_FOR_CODE` diagnostics to trigger connections (keys), by connector rule 17 (feature spec 16,
 * 4.3): `resourceId` is the function and the message contains the connection's alias and `'<source id>'`. Older
 * runs may point to `/connections/<i>` (that connection) or name the event source as `resourceId`. When several
 * connections match, the ones whose alias and other endpoint the message mentions win (both, else either, else the
 * function name), then the ones whose source's description waits for the code (`waitsForCode`; without a
 * description, whose rule needs a function name).
 */
export function waitingTriggerKeys(
  diagnostics: readonly Diagnostic[] | undefined,
  doc: BlueprintDocument,
  types: ReadonlyMap<string, ResourceType>,
  waitsForCode?: (connection: BlueprintConnection) => boolean | undefined,
): Set<string> {
  const keys = new Set<string>();
  const connections = doc.connections ?? [];
  const triggers = connections.filter((c) => c.type === 'triggers');
  for (const diagnostic of diagnostics ?? []) {
    if (diagnostic.code !== TRIGGER_WAITING_FOR_CODE) continue;
    const [head, index] = parsePointer(diagnostic.path ?? '');
    if (head === 'connections' && index !== undefined && /^\d+$/.test(index)) {
      const connection = connections[Number(index)];
      if (connection?.type === 'triggers') {
        keys.add(connectionKey(connection));
        continue;
      }
    }
    let candidates = triggers.filter(
      (c) =>
        !diagnostic.resourceId ||
        c.targetId === diagnostic.resourceId ||
        c.sourceId === diagnostic.resourceId,
    );
    if (candidates.length > 1) {
      const message = diagnostic.message ?? '';
      const other = (c: BlueprintConnection) =>
        c.targetId === diagnostic.resourceId ? c.sourceId : c.targetId;
      const byAlias = (c: BlueprintConnection) => mentionsWord(message, effectiveAlias(c));
      const byOther = (c: BlueprintConnection) => message.includes(`'${other(c)}'`);
      const byFunction = (c: BlueprintConnection) => mentionsWord(message, c.options?.functionName);
      const tests = [
        (c: BlueprintConnection) => byAlias(c) && byOther(c),
        (c: BlueprintConnection) => byAlias(c) || byOther(c),
        byFunction,
      ];
      for (const test of tests) {
        const narrowed = candidates.filter(test);
        if (narrowed.length > 0) {
          candidates = narrowed;
          break;
        }
      }
    }
    if (candidates.length > 1) {
      const resourceType = (id: string) => doc.resources.find((r) => r.id === id)?.type;
      const waits = candidates.filter(
        (c) =>
          waitsForCode?.(c) ??
          !!wiringRule(types, resourceType(c.sourceId), resourceType(c.targetId), 'triggers')
            ?.requiresFunctionName,
      );
      if (waits.length > 0) candidates = waits;
    }
    for (const c of candidates) keys.add(connectionKey(c));
  }
  return keys;
}

export interface AzureResourceId {
  subscriptionId: string;
  resourceGroup: string;
  name: string;
}

/** Parses `/subscriptions/<sub>/resourceGroups/<rg>/providers/<namespace>/<type>/<name>`. */
export function parseAzureResourceId(id: string | null | undefined): AzureResourceId | null {
  const match = /^\/subscriptions\/([^/]+)\/resourceGroups\/([^/]+)\/providers\/[^/]+\/[^/]+\/([^/]+)/i.exec(
    id ?? '',
  );
  return match ? { subscriptionId: match[1], resourceGroup: match[2], name: match[3] } : null;
}

/** What the user's pipeline needs to deploy a function app's code (spec 13, 4.2). */
export interface DeployTarget {
  name: string | null;
  resourceGroup: string | null;
  subscriptionId: string | null;
}

export function deployTarget(resource: DeploymentResource | undefined): DeployTarget {
  const parsed = parseAzureResourceId(resource?.primaryCloudId);
  const output = (key: string) => {
    const value = resource?.outputs?.[key];
    return typeof value === 'string' && value ? value : null;
  };
  return {
    name: output('name') ?? parsed?.name ?? null,
    resourceGroup: output('resourceGroup') ?? parsed?.resourceGroup ?? null,
    subscriptionId: parsed?.subscriptionId ?? null,
  };
}

export const PIPELINE_ROLES = [
  { role: 'Website Contributor', scope: 'function' },
  { role: 'Storage Blob Data Contributor', scope: 'container' },
] as const;

export function deployCommands(target: DeployTarget): string[] {
  const name = target.name ?? '<function-app-name>';
  const group = target.resourceGroup ?? '<resource-group>';
  return [
    `func azure functionapp publish ${name}`,
    `az functionapp deploy --resource-group ${group} --name ${name} --src-path app.zip --type zip`,
  ];
}
